import React, { useEffect, useState } from 'react';
import { Backdrop, Box } from '@mui/material';
import { useImmer } from 'use-immer';
import { GridRowId } from '@mui/x-data-grid-pro';
import { AppHeader } from '../../../../components/app-header';
import { Container } from '../../../../components/container';
import { PageTitleZoneNext } from '../../../../components/page-title-zone';
import { CvResultPanel } from './components/cv-result-panel';
import { CvSearchCity, CvSearchState, DriverLicensesType, getCities, getDriversLicense } from './interfaces';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { CvSearchMunicipality, CvSearchOption, CvSearchRequestDto, CvSearchResultDto, EducationStatus, QualificationStatus } from '../../../../api-service/clients';
import { CircularProgressCentered } from '../../../../components/circular-progress-centered';
import { useMetaTitle } from '../../../../hooks';
import { win } from '../../../../utilities';
import { authorityAreaPathName, citizenAreaPathName, citizenCvViewerPathName, cvJobsearchTabPathName } from '../citizen-authority-area-pathnames';
import { CvSearchDrawer } from './components/cv-search-drawer';
import { CvFurtherSearchDialog } from './components/cv-further-search-dialog';

const cvSearchClient = moduleClientFactory.createCitizenCvSearchClient();
const citizenClient = moduleClientFactory.createCitizenClient();

const initialState = {
  occupations: [],
  qualifications: [],
  educations: [],
  languages: [],
  cities: [],
  selectedOccupations: [],
  selectedQualifications: [],
  selectedFreeTextEducations: [],
  selectedFreeTextOccupations: [],
  selectedFreeTextQualifications: [],
  selectedLanguages: [],
  selectedDriversLicenses: [],
  showOnlyJobReady: true,
  showOnlyFlexjobVisitated: false,
  showOnlyJobcenter: false,
  educationStatus: EducationStatus.NotSpecified,
  qualificationStatus: QualificationStatus.NotSpecified,
  freetextSearch: '',
  numberOfKm: 5,
} as CvSearchState;

export function CvSearch() {
  useMetaTitle('CV Søgning');

  const [state, setState] = useImmer<CvSearchState>(initialState);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CvSearchResultDto>({} as CvSearchResultDto);
  const [openedCvCprs, setOpenedCvCprs] = useState<string[]>([]);
  const [municipalities, setMunicipalities] = useState<CvSearchMunicipality[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [enablePaging, setEnablePaging] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPageSize, setCurrentPageSize] = useState<number>(20);

  function handleSelectionModelChanged(selectionModelChange: GridRowId[]): void {
    setSelectionModel(selectionModelChange);
  }
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);

  useEffect(() => {
    loadOptionData();
  }, []);

  useEffect(() => {
    if (enablePaging) {
      handleChangePage(currentPageNumber, currentPageSize);
    }
  }, [currentPageNumber, currentPageSize]);

  async function handleChangePage(pageNumber: number, pageSize: number) {
    try {
      setIsLoadingMore(true);
      await performSearch(pageNumber, pageSize);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  }

  async function loadOptionData() {
    try {
      setIsLoading(true);
      const optionData = await cvSearchClient.getCvSearchOptions();
      const cities = getCities();

      setState(draft => {
        draft.occupations = optionData.occupations || [];
        draft.qualifications = optionData.qualifications || [];
        draft.educations = optionData.educations || [];
        draft.languages = optionData.languages || [];
        draft.cities = cities;
      });

      const municipalityResult = await cvSearchClient.getMunicipalities();
      setMunicipalities(municipalityResult.municipalities || []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSearchClick() {
    try {
      setIsSearching(true);
      await performSearch(currentPageNumber, currentPageSize);
      setEnablePaging(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  }

  async function performSearch(pageNumber: number, pageSize: number) {
    const {
      selectedDriversLicenses,
      selectedFreeTextEducations,
      selectedFreeTextOccupations,
      selectedFreeTextQualifications,
      selectedLanguages,
      freetextSearch,
      selectedOccupations,
      selectedQualifications,
      selectedCity,
      numberOfKm,
      showOnlyFlexjobVisitated,
      showOnlyJobReady,
      showOnlyJobcenter,
      qualificationStatus,
      educationStatus,
    } = state;
    const request: CvSearchRequestDto = {
      pageNumber,
      showOnlyFlexjobVisitated,
      showOnlyJobReady,
      showOnlyJobcenter,
      jobTitles: selectedOccupations.map(x => x.optionName || ''),
      qualifications: selectedQualifications.map(x => x.optionName || ''),
      searchString: freetextSearch,
      freeSearchEducations: selectedFreeTextEducations.map(x => x.optionName || ''),
      freeSearchJobTitles: selectedFreeTextOccupations.map(x => x.optionName || ''),
      freeSearchQualifications: selectedFreeTextQualifications.map(x => x.optionName || ''),
      languages: selectedLanguages.map(x => x.optionName || ''),
      postalCode: selectedCity?.zipCode,
      searchRadius: selectedCity ? numberOfKm : undefined,
      driversLicenses: selectedDriversLicenses.map(x => getDriversLicense(x)),
      qualificationStatus: qualificationStatus || QualificationStatus.NotSpecified,
      educationStatus: educationStatus || EducationStatus.NotSpecified,
      pageSize,
    };

    const res = await cvSearchClient.performCvSearch({ cvSearchRequest: request });
    setResult(res.cvSearchResult || ({} as CvSearchResultDto));
  }

  function handleReset() {
    setState(draft => {
      draft.selectedOccupations = [];
      draft.selectedQualifications = [];
      draft.selectedFreeTextEducations = [];
      draft.selectedFreeTextOccupations = [];
      draft.selectedFreeTextQualifications = [];
      draft.selectedLanguages = [];
      draft.selectedDriversLicenses = [];
      draft.showOnlyJobReady = true;
      draft.showOnlyFlexjobVisitated = false;
      draft.showOnlyJobcenter = false;
      draft.educationStatus = EducationStatus.NotSpecified;
      draft.qualificationStatus = QualificationStatus.NotSpecified;
      draft.freetextSearch = '';
      draft.numberOfKm = 5;
      draft.selectedCity = undefined;
    });
    setOpenedCvCprs([]);
    setEnablePaging(false);
    setResult({} as CvSearchResultDto);
  }

  function handleNumberOfKmChange(km: number) {
    setState(draft => {
      draft.numberOfKm = km;
    });
  }

  function handleOccupationChange(value: CvSearchOption[]) {
    setState(draft => {
      draft.selectedOccupations = value;
    });
  }

  function handleQualificationChange(value: CvSearchOption[]) {
    setState(draft => {
      draft.selectedQualifications = value;
    });
  }

  function handleLanguageChange(value: CvSearchOption[]) {
    setState(draft => {
      draft.selectedLanguages = value;
    });
  }

  function handleDriversLicenseChange(value: DriverLicensesType[]) {
    setState(draft => {
      draft.selectedDriversLicenses = value;
    });
  }

  function handleCityChange(value?: CvSearchCity) {
    setState(draft => {
      draft.selectedCity = value;
    });
  }

  function handleCheckboxChange(name: string, checked: boolean) {
    setState((draft: any) => {
      draft[name] = checked;
    });
  }

  function handleFreetextChange(value?: string) {
    setState(draft => {
      draft.freetextSearch = value;
    });
  }

  function handleEducationStatusChange(newValue: EducationStatus) {
    setState(draft => {
      draft.educationStatus = newValue;
    });
  }

  function handleQualificationStatusChange(newValue: QualificationStatus) {
    setState(draft => {
      draft.qualificationStatus = newValue;
    });
  }

  async function handleOpenCitizenCv(citizenCpr: string) {
    try {
      setIsLoading(true);

      setOpenedCvCprs([...openedCvCprs, citizenCpr]);
      const citizenExists = await citizenClient.getByCpr({ citizenCpr });
      if (citizenExists.statusCode === 200) {
        const citizen = await citizenClient.getOrAddCitizen({ citizenCpr });
        const citizenId = citizen?.citizenId;
        if (citizenId) {
          win.open(`${citizenAreaPathName}/${authorityAreaPathName}/${citizenId}/${cvJobsearchTabPathName}/${citizenCvViewerPathName}`);
          return;
        }
      }

      const pdfContent = await cvSearchClient.getCvAsPdfByCprNumber({ citizenCpr });
      const pdfFromEditableContent = new Blob([pdfContent.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfFromEditableContent);
      link.target = '_blank';
      link.click();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const [openFurtherSearchDialog, setOpenFurtherSearchDialog] = useState(false);

  function handleFurtherSearchClick() {
    setOpenFurtherSearchDialog(true);
  }

  function handlePageChange(page: number) {
    setCurrentPageNumber(page + 1);
  }

  function handlePageSizeChange(pageSize: number) {
    setCurrentPageSize(pageSize);
  }

  return (
    <Container>
      <AppHeader />
      <PageTitleZoneNext entityName="CV Søgning" title="Søgning" />

      <Box display="flex" gap={1} sx={{ paddingTop: 2, paddingLeft: 2 }}>
        <Box display="flex" flexDirection="column" gap={1} flexGrow={1}>
          {openFurtherSearchDialog && <CvFurtherSearchDialog selectedCitizens={selectionModel} onFurtherSearchClick={() => setOpenFurtherSearchDialog(false)} />}
          <CvResultPanel
            municipalities={municipalities}
            openedCvCprs={openedCvCprs}
            totalCvs={result.resultCount}
            citizenCvs={result.citizenCvItems || []}
            openCitizenCv={handleOpenCitizenCv}
            selectionModel={selectionModel}
            onSelectionModelChanged={handleSelectionModelChanged}
            onFurtherSearchClick={handleFurtherSearchClick}
            onPageChange={handlePageChange}
            currentPageNumber={currentPageNumber - 1}
            isLoadingMore={isLoadingMore}
            pageSize={currentPageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>
        <CvSearchDrawer
          onNumberOfKmChange={handleNumberOfKmChange}
          searchPanel={state}
          onSearchClick={handleSearchClick}
          onResetClick={handleReset}
          onOccupationChange={handleOccupationChange}
          onQualificationChange={handleQualificationChange}
          onLanguageChange={handleLanguageChange}
          onDriversLicenseChange={handleDriversLicenseChange}
          onCityChange={handleCityChange}
          onCheckboxChange={handleCheckboxChange}
          onEducationStatusChange={handleEducationStatusChange}
          onQualificationStatusChange={handleQualificationStatusChange}
          onFreetextChange={handleFreetextChange}
          isSearching={isSearching}
        />
      </Box>
      <Backdrop open={isLoading}>
        <CircularProgressCentered />
      </Backdrop>
    </Container>
  );
}
